import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Container, Typography, Paper, Stack } from '@mui/material';
import QrScanner from './QrScanner';
import QrForm from './QrForm';
import QrTable from './QrTable';

import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import StoreSelector from '../../components/StoreSelector/StoreSelector';

import { useUserAuth } from '../../context/UserAuthContext';

import './QrCodeMapper.css';

export default function QrCodeMapper() {
  const {
    user: firebaseUser,
    selectedStore,
    setSelectedStore,
  } = useUserAuth();
  const [loading, setLoading] = useState(false);

  const [qrCode, setQrCode] = useState('');
  const [mappings, setMappings] = useState([]);
  const [stores, setStores] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    cell: '',
    role: '',
    defaultStore: '',
    storeName: '',
    department: '', // This field is only visible when the role is 'Basecamp'
    selectedStore: selectedStore,
  });
  const handleGetStores = useCallback(async () => {
    try {
      setLoading(true);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/user/stores/${firebaseUser.email}`,
        {
          headers: {
            Authorization: `Bearer ${firebaseUserIdToken}`,
          },
          'Content-Type': 'application/json',
        }
      );
      setStores(response.data.stores);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stores from db:', error);
      setLoading(false);
    }
  }, [firebaseUser]);

  const handleGetUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/user/${firebaseUser.email}/info`,
        {
          headers: {
            Authorization: `Bearer ${firebaseUserIdToken}`,
          },
          'Content-Type': 'application/json',
        }
      );
      const userInfo = response.data.userInfo;
      const defaultStoreId = userInfo.defaultStore || userInfo.selectedStore;

      // Set the selectedStore if not already set
      if (!selectedStore && defaultStoreId) {
        setSelectedStore(defaultStoreId);
      }

      setFormData({
        ...userInfo,
        defaultStore: selectedStore || defaultStoreId,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user info from db:', error);
      setLoading(false);
    }
  }, [firebaseUser, selectedStore, setSelectedStore]);

  const fetchMappings = useCallback(
    async (storeId = selectedStore) => {
      if (!storeId) return;

      setLoading(true);
      try {
        const idToken = await firebaseUser.getIdToken();

        const response = await axios.get(
          `/hd-api/herd-serve?storeId=${storeId}`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        setMappings(response.data);
      } catch (error) {
        console.error('Failed to fetch mappings:', error);
        // Show user-friendly error message
        if (error.response?.status === 400) {
          console.error('Invalid store ID provided');
        } else if (error.message?.includes('terminated')) {
          console.error('Database connection issue, please try again');
        }
      } finally {
        setLoading(false);
      }
    },
    [firebaseUser, selectedStore]
  );

  useEffect(() => {
    if (firebaseUser.getIdToken) {
      handleGetStores();
      handleGetUserInfo();
    }
  }, [firebaseUser, handleGetStores, handleGetUserInfo]);

  useEffect(() => {
    if (selectedStore && firebaseUser.getIdToken) {
      fetchMappings(selectedStore);
    }
  }, [selectedStore, firebaseUser, fetchMappings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const s = stores.find((s) => s.storeId === value);
    setSelectedStore(s.storeId);
    setFormData({
      ...formData,
      [name]: value,
      storeName: s.storeName,
      selectedStore: s.storeId,
    });
  };

  return (
    <Box p={2}>
      <Header isTabletOrMobile={true} />
      <Container maxWidth='xl' style={{ textAlign: 'center' }}>
        <Logo isTabletOrMobile={true} showAlert={false} />

        <Typography variant='h5' mb={2} style={{ fontFamily: 'Lato' }}>
          HerdServe Mapper
        </Typography>
        <StoreSelector
          stores={stores}
          formData={formData}
          handleChange={handleChange}
        />
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <QrScanner
              onScan={(url) => {
                const params = new URLSearchParams(url.split('?')[1]);
                const id = params.get('id');
                if (id) setQrCode(id);
              }}
            />
            <QrForm
              qrCode={qrCode}
              onSuccess={() => fetchMappings(selectedStore)}
              firebaseUser={firebaseUser}
              setLoading={setLoading}
              selectedStore={selectedStore}
            />
          </Stack>
        </Paper>

        <QrTable
          mappings={mappings}
          onRefresh={() => fetchMappings(selectedStore)}
          firebaseUser={firebaseUser}
          setLoading={setLoading}
          loading={loading}
          selectedStore={selectedStore}
        />
      </Container>
    </Box>
  );
}
